import { QueryClient } from "@tanstack/react-query";
import { isRedirect, redirect } from "@tanstack/react-router";

import { cancelPreviewQuery } from "@/modules/coaching/shared/queries/getCancelPreviewQuery";
import { ROUTES } from "@/modules/shared/consts/routes";
import { DEFAULT_ENTRY_POINT } from "@/modules/tracking/consts/EntryPoints";
import { SHORT_URL_EVENTS } from "@/modules/tracking/consts/Events";
import { ShortUrlFlow } from "@/modules/tracking/consts/ShortUrl";
import { abandonPreviousShortUrlFlowIfDifferent } from "@/modules/tracking/utils/abandonPreviousShortUrlFlow";
import {
  getShortUrlResolutionFromError,
  getShortUrlResolutionFromSuccess,
} from "@/modules/tracking/utils/getShortUrlResolutionStatus";
import { readSourceFromLocation } from "@/modules/tracking/utils/readSourceFromLocation";
import { registerSuperProperties } from "@/modules/tracking/utils/registerSuperProperties";
import {
  endShortUrlFlowSession,
  isSameShortUrlFlowVisit,
  startShortUrlFlowSession,
} from "@/modules/tracking/utils/shortUrlFlowSession";
import { trackShortUrlEvent } from "@/modules/tracking/utils/trackShortUrlEvent";

type CancelSessionLoaderArgs = {
  context: { queryClient: QueryClient };
  params: { cancelUuid: string };
};

export const cancelSessionLoader = async ({
  context: { queryClient },
  params: { cancelUuid },
}: CancelSessionLoaderArgs) => {
  const source = readSourceFromLocation();
  const isRevalidation = isSameShortUrlFlowVisit(
    ShortUrlFlow.Cancel,
    cancelUuid,
  );

  try {
    const data = await queryClient.fetchQuery(cancelPreviewQuery(cancelUuid));

    registerSuperProperties({
      entry_point: DEFAULT_ENTRY_POINT,
      customer_id: data.session.customerId,
    });

    if (!isRevalidation) {
      abandonPreviousShortUrlFlowIfDifferent(ShortUrlFlow.Cancel, cancelUuid);

      const resolution = getShortUrlResolutionFromSuccess();
      trackShortUrlEvent(SHORT_URL_EVENTS.ACCESS_CODE_RESOLVED, {
        access_code: cancelUuid,
        session_id: String(data.session.id),
        coach_id: String(data.session.coachId),
        resolution_status: resolution.resolution_status,
        http_status: resolution.http_status,
        correlation_id: null,
      });

      startShortUrlFlowSession({
        flow: ShortUrlFlow.Cancel,
        accessCode: cancelUuid,
        sessionId: String(data.session.id),
        source,
        correlationId: null,
      });

      trackShortUrlEvent(SHORT_URL_EVENTS.FLOW_ENTERED, {
        flow: ShortUrlFlow.Cancel,
        session_id: String(data.session.id),
        coach_id: String(data.session.coachId),
        source,
      });
    }

    return { data };
  } catch (error) {
    if (isRedirect(error)) {
      throw error;
    }

    if (!isRevalidation) {
      const resolution = getShortUrlResolutionFromError(error);
      trackShortUrlEvent(SHORT_URL_EVENTS.ACCESS_CODE_RESOLVED, {
        access_code: cancelUuid,
        session_id: null,
        coach_id: null,
        resolution_status: resolution.resolution_status,
        http_status: resolution.http_status,
        correlation_id: null,
      });
    }
    endShortUrlFlowSession();

    throw redirect({ to: ROUTES.notFound });
  }
};
